import React, { useState, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAutosave } from "@/lib/useAutosave";
import { useDraftBackup, DraftRecoverBanner } from "@/components/app/DraftRecover";
import { ClipboardCheck, X, Save, Loader2, Plus, Trash2 } from "lucide-react";
import FirmasEditor from "@/components/app/FirmasEditor";
import AttachFiles from "@/components/app/AttachFiles";

const DEFAULT_HEADERS = {
  alumno: "ALUMNO",
  c1: "LENGUAJES",
  c2: "SABERES Y PENSAMIENTO CIENTÍFICO",
  c3: "ÉTICA, NATURALEZA Y SOCIEDADES",
  c4: "DE LO HUMANO Y LO COMUNITARIO",
};
const COL_DOTS = ["#c0392b", "#1f3a93", "#27ae60", "#7d3c98"];

const Field = ({ label, value, onChange, testid, placeholder }) => (
  <div>
    <label className="text-[10px] uppercase tracking-[0.15em] text-nardo-500 font-semibold">{label}</label>
    <input value={value} onChange={onChange} data-testid={testid} placeholder={placeholder}
      className="mt-1 w-full border border-nardo-200 rounded-lg px-2.5 py-1.5 text-sm text-navy outline-none focus:ring-2 focus:ring-navy/30" />
  </div>
);

export default function PlaneacionEvaluacion({ initial, onSaved, onCancel }) {
  const [titulo, setTitulo] = useState(initial?.proyecto || "");
  const [saving, setSaving] = useState(false);
  const [savedId, setSavedId] = useState(initial?.id || null);
  const [d, setD] = useState(() => ({
    title: initial?.data?.title || "REPORTE DE EVALUACIÓN",
    subtitle: initial?.data?.subtitle || "",
    trimestre: initial?.data?.trimestre || "1",
    headers: { ...DEFAULT_HEADERS, ...(initial?.data?.headers || {}) },
    rows: Array.isArray(initial?.data?.rows) && initial.data.rows.length
      ? initial.data.rows
      : [{ alumno: "", c1: "", c2: "", c3: "", c4: "" }],
    firma_docente_name: initial?.data?.firma_docente_name || "",
    firma_docente_rol: initial?.data?.firma_docente_rol || "Docente",
    firma_director_name: initial?.data?.firma_director_name || "",
    firma_director_rol: initial?.data?.firma_director_rol || "Director(a)",
    firma_coord_name: initial?.data?.firma_coord_name || "",
    firma_coord_rol: initial?.data?.firma_coord_rol || "Coordinador(a)",
    _files: initial?.data?._files || {},
  }));

  const set = (k) => (e) => setD((s) => ({ ...s, [k]: e.target.value }));
  const setFiles = (arr) => setD((s) => ({ ...s, _files: { ...(s._files || {}), general: arr } }));
  const setHeader = (k) => (e) => setD((s) => ({ ...s, headers: { ...s.headers, [k]: e.target.value } }));
  const setRow = (i, k) => (e) => setD((s) => ({ ...s, rows: s.rows.map((r, idx) => (idx === i ? { ...r, [k]: e.target.value } : r)) }));
  const addRow = () => setD((s) => ({ ...s, rows: [...s.rows, { alumno: "", c1: "", c2: "", c3: "", c4: "" }] }));
  const delRow = (i) => setD((s) => ({ ...s, rows: s.rows.filter((_, idx) => idx !== i) }));

  const savedSnap = useRef("");
  const savingRef = useRef(false);
  const draft = useDraftBackup({ tipo: "evaluacion", initialId: initial?.id });
  const applyDraft = (p) => { if (!p) return; setTitulo(p.titulo || ""); if (p.d) setD(p.d); };
  const save = async (silent = false) => {
    const body = { proyecto: titulo.trim() || "Reporte sin título", tipo: "evaluacion", color: "#112240", formato: 5, data: d };
    const snap = JSON.stringify(body);
    if (silent && snap === savedSnap.current) return;
    if (savingRef.current) return;
    savingRef.current = true;
    if (!silent) setSaving(true);
    try {
      if (savedId) await api.put(`/docente/planeaciones/${savedId}`, body);
      else { const { data: res } = await api.post("/docente/planeaciones", body); setSavedId(res.id); }
      savedSnap.current = snap;
      draft.clear();
      if (!silent) onSaved();
    } catch (e) { if (!silent) toast.error(e?.response?.data?.detail || "No se pudo guardar"); }
    finally { savingRef.current = false; if (!silent) setSaving(false); }
  };
  const baseWatch = useRef();
  const watch = JSON.stringify(d) + "|" + titulo;
  if (baseWatch.current === undefined) baseWatch.current = watch;
  useAutosave(watch, () => { draft.backup({ titulo, d }); save(true); }, { enabled: watch !== baseWatch.current });

  const colKeys = ["c1", "c2", "c3", "c4"];

  return (
    <div className="fixed inset-0 z-[80] bg-slate-900/80 flex items-start justify-center overflow-y-auto p-4" onClick={onCancel} data-testid="pe-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full my-8 max-w-6xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 rounded-t-2xl text-white bg-navy">
          <div className="font-display font-bold flex items-center gap-2"><ClipboardCheck className="w-4 h-4" /> {initial?.id ? "Editar" : "Nuevo"} reporte de evaluación</div>
          <button onClick={onCancel} className="text-white/80 hover:text-white p-1"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-4 sm:p-5 space-y-4">
          <DraftRecoverBanner pending={draft.pending} onRecover={() => { applyDraft(draft.pending); draft.consumed(); }} onDismiss={draft.dismiss} />
          <input value={titulo} onChange={(e) => setTitulo(e.target.value)} placeholder="Nombre del reporte (para tu lista, ej. 1°A Trimestre 2)" data-testid="pe-titulo"
            className="w-full border border-nardo-200 rounded-lg px-3 py-2.5 text-sm text-navy outline-none focus:border-navy font-semibold" />

          <div className="grid sm:grid-cols-2 gap-3 p-3 rounded-lg border border-dashed border-nardo-300">
            <div className="sm:col-span-2 text-[10px] uppercase tracking-[0.2em] text-nardo-500 font-semibold">Encabezado del formato (universal · editable)</div>
            <Field label="Título" value={d.title} onChange={set("title")} testid="pe-title" placeholder="REPORTE DE EVALUACIÓN" />
            <Field label="Subtítulo (opcional)" value={d.subtitle} onChange={set("subtitle")} testid="pe-subtitle" placeholder="Preescolar · Ciclo 2026-2027" />
            <p className="sm:col-span-2 text-[10px] text-nardo-400">Este formato se genera sin logo, sin marca de agua y sin nombre de colegio.</p>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-nardo-500 font-semibold mb-1.5">Trimestre</div>
            <div className="flex flex-wrap gap-2" data-testid="pe-trimestres">
              {["1", "2", "3"].map((n) => (
                <button key={n} type="button" onClick={() => setD((s) => ({ ...s, trimestre: n }))} data-testid={`pe-tri-${n}`}
                  className={`text-sm font-semibold px-4 py-2 rounded-lg border transition-colors ${d.trimestre === n ? "bg-navy text-white border-navy" : "border-nardo-200 text-nardo-600 hover:bg-nardo-50"}`}>
                  Trimestre {n}
                </button>
              ))}
            </div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 p-3 rounded-lg bg-nardo-50">
            <div className="sm:col-span-2 lg:col-span-5 text-[10px] uppercase tracking-[0.2em] text-nardo-500 font-semibold">Encabezados de columnas (editables)</div>
            <Field label="Columna alumno" value={d.headers.alumno} onChange={setHeader("alumno")} testid="pe-h-alumno" />
            {colKeys.map((k, i) => (
              <div key={k}>
                <label className="text-[10px] uppercase tracking-[0.15em] text-nardo-500 font-semibold flex items-center gap-1">
                  <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: COL_DOTS[i] }} /> Campo {i + 1}
                </label>
                <input value={d.headers[k]} onChange={setHeader(k)} data-testid={`pe-h-${k}`}
                  className="mt-1 w-full border border-nardo-200 rounded-lg px-2.5 py-1.5 text-sm text-navy outline-none focus:ring-2 focus:ring-navy/30" />
              </div>
            ))}
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-[11px] uppercase tracking-[0.2em] text-nardo-500 font-semibold">Alumnos y evaluación</div>
              <button type="button" onClick={addRow} data-testid="pe-add-row" className="text-xs font-semibold inline-flex items-center gap-1 text-navy border border-navy/30 hover:bg-navy hover:text-white px-2.5 py-1.5 rounded-lg transition-colors"><Plus className="w-3.5 h-3.5" /> Agregar alumno</button>
            </div>
            <div className="space-y-2" data-testid="pe-rows">
              {d.rows.map((r, i) => (
                <div key={i} className="grid grid-cols-1 lg:grid-cols-6 gap-2 items-start border border-nardo-200 rounded-lg p-2" data-testid={`pe-row-${i}`}>
                  <input value={r.alumno} onChange={setRow(i, "alumno")} placeholder={d.headers.alumno} data-testid={`pe-row-${i}-alumno`}
                    className="lg:col-span-2 w-full border border-nardo-200 rounded-md px-2 py-1.5 text-sm text-navy outline-none focus:ring-2 focus:ring-navy/30" />
                  {colKeys.map((k) => (
                    <input key={k} value={r[k]} onChange={setRow(i, k)} data-testid={`pe-row-${i}-${k}`}
                      className="w-full border border-nardo-200 rounded-md px-2 py-1.5 text-sm text-navy outline-none focus:ring-2 focus:ring-navy/30" />
                  ))}
                  <div className="flex justify-end lg:col-span-6">
                    <button type="button" onClick={() => delRow(i)} data-testid={`pe-del-row-${i}`} className="text-[11px] inline-flex items-center gap-1 text-red-500 hover:text-red-700"><Trash2 className="w-3.5 h-3.5" /> Quitar</button>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-nardo-400 mt-2">El PDF completa filas en blanco si dejas pocos alumnos, para que puedas imprimirlo y llenarlo a mano si lo prefieres.</p>
          </div>

          <AttachFiles value={d._files?.general || []} onChange={setFiles} base="pe-attach" />

          <FirmasEditor d={d} set={set} base="pe-firmas" color="#112240" />

          <div className="flex justify-end gap-2 pt-1">
            <span className="mr-auto self-center text-[11px] text-nardo-400" data-testid="pe-autosave-hint">Autoguardado activo</span>
            <button onClick={onCancel} className="px-4 py-2 rounded-lg border border-nardo-200 text-sm text-nardo-600 hover:bg-nardo-50" data-testid="pe-cancel">Cerrar</button>
            <button onClick={() => save(false)} disabled={saving} data-testid="pe-save" className="px-4 py-2 rounded-lg text-white text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50 bg-navy hover:bg-navy-light">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar cambios y contenido
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
